import { useSelector } from "react-redux";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationDetail } from "../../Organizations/_models/Organization";

// Who may administer an organization, mirroring the backend guard
// (`_require_owner` in api_sentio/views/api/organization.py). Membership used
// to be enough; since adding a member grants admin on every location of the
// organization, the managing operations — rename, add/remove member, remove
// location — are the owner's alone and answer error 780 to anyone else.
//
// The null owner is not an oversight: organizations backfilled without members
// and those whose owner was deleted (SET_NULL) have nobody to enforce, and the
// backend lets any member act on them. Gating the UI on a plain
// `ownerUid === myUid` would lock those organizations out of the web app for
// everybody, so `canManage` reproduces the exemption.
export function canManageOrganization(
    detail: OrganizationDetail | null,
    myUid: string | null,
): boolean {
    if (!detail) return false;
    if (!detail.ownerUid) return true;
    return !!myUid && detail.ownerUid === myUid;
}

// Strict ownership, with no null-owner exemption: deleting the organization
// (error 776) compares against the owner unconditionally on the backend, so an
// organization without owner cannot be deleted by anyone.
export function isOrganizationOwner(
    detail: OrganizationDetail | null,
    myUid: string | null,
): boolean {
    return !!detail && !!myUid && detail.ownerUid === myUid;
}

export function useOrganizationPermissions(): {
    detail: OrganizationDetail | null;
    canManage: boolean;
    isOwner: boolean;
} {
    const detail: OrganizationDetail | null = useSelector(
        Selectors.getOrganizationSelectedDetail,
    );
    const myUid: string | null = useSelector(Selectors.getUserUid);

    return {
        detail,
        canManage: canManageOrganization(detail, myUid),
        isOwner: isOrganizationOwner(detail, myUid),
    };
}
